import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './Auth0Context';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // Use employee_id (database user ID) instead of Auth0 user ID
      const userDatabaseId = user.employee_id || user.id;
      const isAdmin = user.role === 'administrator' || user.role === 'admin';

      console.log('🔍 Fetching notifications for user:', {
        auth0Id: user.id,
        employeeId: user.employee_id,
        usingId: userDatabaseId,
        isAdmin: isAdmin,
        userObject: user
      });

      // Admin users get all notifications, regular users get only their own
      const apiUrl = isAdmin
        ? '/api/notifications'
        : `/api/notifications?user_id=${userDatabaseId}`;

      const response = await axios.get(apiUrl);
      console.log('✅ Notifications fetched:', response.data?.length || 0, 'notifications', isAdmin ? '(all notifications for admin)' : '(user-specific)');

      // Ensure response.data is always an array
      const notificationsData = Array.isArray(response.data) ? response.data : [];
      setNotifications(notificationsData);

      // Count unread notifications
      const unread = notificationsData.filter(notification => !notification.read).length;
      setUnreadCount(unread);
      console.log('📊 Unread count updated:', unread);
    } catch (error) {
      console.error('❌ Error fetching notifications:', error);
      console.error('❌ User context:', user);
      // Set empty array on error to prevent crashes
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    console.log('🔍 Attempting to mark notification as read:', notificationId);
    console.log('🔍 Current user object:', user);
    console.log('🔍 Using user ID:', user.employee_id || user.id);
    
    try {
      // Optimistically update the local state first for immediate visual feedback
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true }
            : notification
        )
      );
      
      // Update unread count immediately
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      const response = await axios.patch(`/api/notifications/${notificationId}`, { read: true });
      console.log('✅ Mark as read response:', response.data);
      console.log('✅ Local state updated for notification:', notificationId);
      
      // Fetch fresh data to ensure consistency
      fetchNotifications();
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      console.error('❌ Full error object:', error);
      
      // Revert the optimistic update on error
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: false }
            : notification
        )
      );
      setUnreadCount(prev => prev + 1);
      
      // Additional debugging
      if (error.response) {
        console.error('❌ Response status:', error.response.status);
        console.error('❌ Response headers:', error.response.headers);
      }
    }
  }, [user, fetchNotifications]);

  // Mark all notifications as read by iterating through each unread notification
  const markAllAsRead = useCallback(async () => {
    console.log('🔍 Attempting to mark all visible unread notifications as read');
    try {
      // Get all unread notifications from current state
      const unreadNotifications = notifications.filter(notification => !notification.read);
      console.log(`🔍 Found ${unreadNotifications.length} unread notifications to mark as read`);

      if (unreadNotifications.length === 0) {
        console.log('✅ No unread notifications to mark');
        return;
      }

      // Optimistically update the local state
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: true }))
      );
      setUnreadCount(0);

      // Mark each notification as read individually using the existing markAsRead function
      const markPromises = unreadNotifications.map(notification => {
        console.log(`🔍 Marking notification ${notification.id} as read`);
        return axios.patch(`/api/notifications/${notification.id}`, { read: true });
      });

      // Wait for all individual mark operations to complete
      const results = await Promise.allSettled(markPromises);

      // Log results
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      console.log(`✅ Mark all as read completed: ${successful} successful, ${failed} failed`);

      if (failed > 0) {
        console.warn('⚠️ Some notifications failed to mark as read, refreshing to get accurate state');
      }

      // Fetch fresh data to ensure consistency
      fetchNotifications();
    } catch (error) {
      console.error('❌ Error marking all notifications as read:', error);
      console.error('❌ Error details:', error.response?.data || error.message);

      // Revert optimistic update on error
      fetchNotifications();
    }
  }, [notifications, fetchNotifications]);

  // Fetch notifications on component mount and set up polling
  useEffect(() => {
    if (user?.id) {
      fetchNotifications();

      // Poll for new notifications every 2 minutes
      const interval = setInterval(fetchNotifications, 120000);

      return () => clearInterval(interval);
    }
  }, [user?.id, fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}; 